import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Input } from '@shadcn/components/ui/input';
import { Label } from '@shadcn/components/ui/label';
import { Button } from '@shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import { Textarea } from '@shadcn/components/ui/textarea';
import { X, Trash2, Copy, Check, Eye, EyeOff } from 'lucide-react';
import {
  NODE_TYPES,
  TRIGGER_TYPES,
  TRIGGER_TYPE_LABELS,
  ERROR_BEHAVIORS,
  ERROR_BEHAVIOR_LABELS,
} from '../../constants';
import ConditionGroupEditor from './conditions/ConditionGroupEditor';
import { buildConditionLabel } from './conditions/conditionUtils';
import TemplateVarButton from './TemplateVarButton';

const TriggerConfigFields = ({ data, eventTypes, onUpdate }) => (
  <div className="space-y-4">
    <div>
      <Label>Trigger Type</Label>
      <Select
        value={data.triggerType || ''}
        onValueChange={(val) => onUpdate({ ...data, triggerType: val, label: TRIGGER_TYPE_LABELS[val] || val })}
      >
        <SelectTrigger className="mt-1">
          <SelectValue placeholder="Select type" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(TRIGGER_TYPES).map(([key, value]) => (
            <SelectItem key={value} value={value}>{TRIGGER_TYPE_LABELS[value]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {data.triggerType === TRIGGER_TYPES.EVENT && (
      <div>
        <Label>Event Name</Label>
        <Select
          value={data.eventName || ''}
          onValueChange={(val) => {
            const event = eventTypes.find(e => (e.name || e.value) === val);
            onUpdate({ ...data, eventName: val, label: event?.label || val });
          }}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select event" />
          </SelectTrigger>
          <SelectContent>
            {eventTypes.map(event => (
              <SelectItem key={event.name || event.value} value={event.name || event.value}>
                {event.label || event.name || event.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}

    {data.triggerType === TRIGGER_TYPES.SCHEDULE && (
      <div>
        <Label>Cron Expression</Label>
        <Input
          className="mt-1"
          value={data.schedule || ''}
          onChange={(e) => onUpdate({ ...data, schedule: e.target.value, label: `Schedule: ${e.target.value}` })}
          placeholder="*/30 * * * *"
        />
        <p className="text-xs text-gray-400 mt-1">e.g. "*/30 * * * *" = every 30 minutes</p>
      </div>
    )}

    {data.triggerType === TRIGGER_TYPES.MANUAL && (
      <p className="text-sm text-gray-500">This rule will only run when manually triggered.</p>
    )}

    {data.triggerType === TRIGGER_TYPES.WEBHOOK && (
      <WebhookConfigFields data={data} onUpdate={onUpdate} />
    )}
  </div>
);

const WebhookConfigFields = ({ data, onUpdate }) => {
  const [copied, setCopied] = React.useState(false);
  const [showSecretInput, setShowSecretInput] = React.useState(!data.hasExistingSecret);
  const [showSecret, setShowSecret] = React.useState(false);

  const { backendUrl } = require('@helpers/consts');
  const webhookPath = (data.webhookPath || '').trim();
  const fullUrl = webhookPath
    ? `${backendUrl}/automation/hooks/${webhookPath}`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Webhook Path</Label>
        <Input
          className="mt-1"
          value={data.webhookPath || ''}
          onChange={(e) => {
            const path = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
            onUpdate({ ...data, webhookPath: path, label: path ? `Webhook: /${path}` : 'Webhook' });
          }}
          placeholder="my-hook"
        />
        <p className="text-xs text-gray-400 mt-1">Lowercase letters, numbers, and hyphens only</p>
      </div>

      {webhookPath && (
        <div>
          <Label>Webhook URL</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={fullUrl}
              readOnly
              className="text-xs font-mono bg-gray-50"
            />
            <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
        </div>
      )}

      <div>
        <Label>Secret (optional)</Label>
        {data.hasExistingSecret && !showSecretInput ? (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded">Secret is set</span>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => setShowSecretInput(true)}
            >
              Change
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 text-red-500 hover:text-red-700"
              onClick={() => {
                onUpdate({ ...data, webhookSecret: '', hasExistingSecret: false });
                setShowSecretInput(true);
              }}
            >
              Remove
            </Button>
          </div>
        ) : (
          <div className="relative mt-1">
            <Input
              type={showSecret ? 'text' : 'password'}
              value={data.webhookSecret || ''}
              onChange={(e) => onUpdate({ ...data, webhookSecret: e.target.value })}
              placeholder="Enter a secret for HMAC verification"
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowSecret(!showSecret)}
            >
              {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {data.webhookSecret || data.hasExistingSecret
            ? 'Callers must send header: x-webhook-signature: sha256=<hex>'
            : 'If set, webhook calls must include an HMAC-SHA256 signature'}
        </p>
      </div>
    </div>
  );
};

const ConditionConfigFields = ({ data, onUpdate, fields }) => {
  const handleGroupChange = (updatedGroup) => {
    onUpdate({
      ...data,
      conditionGroup: updatedGroup,
      label: buildConditionLabel(updatedGroup),
    });
  };

  return (
    <ConditionGroupEditor
      conditionGroup={data.conditionGroup}
      onChange={handleGroupChange}
      fields={fields}
    />
  );
};

const DYNAMIC_SOURCE_FETCHERS = {
  flagTypes: async () => {
    const automationApi = (await import('../../services/automationApi')).default;
    const flagTypes = await automationApi.getFlagTypes();
    return (flagTypes || []).map(ft => ({ value: ft._id, label: ft.name || ft._id }));
  },
  rules: async () => {
    const automationApi = (await import('../../services/automationApi')).default;
    const rules = await automationApi.getRulesForSelect();
    return (rules || []).map(r => ({ value: r._id, label: r.name || r._id }));
  },
};

const ActionConfigFields = ({ data, actionTypes, onUpdate, fields }) => {
  const selectedAction = actionTypes.find(a => (a.type || a.value) === data.actionType);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const fetchedSourcesRef = useRef(new Set());

  // Fetch options for any dynamic_select fields when the selected action changes
  useEffect(() => {
    if (!selectedAction?.configSchema) return;
    const dynamicFields = selectedAction.configSchema.filter(f => f.type === 'dynamic_select' && f.source);
    for (const field of dynamicFields) {
      if (fetchedSourcesRef.current.has(field.source)) continue; // already fetched
      fetchedSourcesRef.current.add(field.source);
      const fetcher = DYNAMIC_SOURCE_FETCHERS[field.source];
      if (fetcher) {
        fetcher().then(options => {
          setDynamicOptions(prev => ({ ...prev, [field.source]: options }));
        }).catch(() => {
          setDynamicOptions(prev => ({ ...prev, [field.source]: [] }));
        });
      }
    }
  }, [selectedAction]);

  return (
    <div className="space-y-4">
      <div>
        <Label>Action Type</Label>
        <Select
          value={data.actionType || ''}
          onValueChange={(val) => {
            const action = actionTypes.find(a => (a.type || a.value) === val);
            onUpdate({ ...data, actionType: val, config: {}, label: action?.label || val });
          }}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select action" />
          </SelectTrigger>
          <SelectContent>
            {actionTypes.map(action => (
              <SelectItem key={action.type || action.value} value={action.type || action.value}>
                {action.label || action.type || action.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedAction?.configSchema?.map(field => (
        <div key={field.name}>
          <Label>{field.label || field.name}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          {field.type === 'select' ? (
            <Select
              value={data.config?.[field.name] || ''}
              onValueChange={(val) => onUpdate({ ...data, config: { ...data.config, [field.name]: val } })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={field.placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label || opt.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : field.type === 'dynamic_select' ? (
            <Select
              value={data.config?.[field.name] || ''}
              onValueChange={(val) => onUpdate({ ...data, config: { ...data.config, [field.name]: val } })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={
                  dynamicOptions[field.source] ? 'Select...' : 'Loading...'
                } />
              </SelectTrigger>
              <SelectContent>
                {(dynamicOptions[field.source] || []).map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label || opt.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : field.type === 'textarea' ? (
            <div className="relative mt-1">
              <Textarea
                value={data.config?.[field.name] || ''}
                onChange={(e) => onUpdate({ ...data, config: { ...data.config, [field.name]: e.target.value } })}
                placeholder={field.placeholder}
              />
              {field.templateVar && (
                <TemplateVarButton
                  fields={fields}
                  onInsert={(path, cursorPos) => {
                    const current = data.config?.[field.name] || '';
                    const tag = `{{${path}}}`;
                    const newValue = cursorPos != null
                      ? current.slice(0, cursorPos) + tag + current.slice(cursorPos)
                      : current + tag;
                    onUpdate({ ...data, config: { ...data.config, [field.name]: newValue } });
                  }}
                />
              )}
            </div>
          ) : field.type === 'number' ? (
            <Input
              className="mt-1"
              type="number"
              value={data.config?.[field.name] || ''}
              onChange={(e) => onUpdate({ ...data, config: { ...data.config, [field.name]: e.target.value === '' ? '' : Number(e.target.value) } })}
              placeholder={field.placeholder}
            />
          ) : (
            <div className="relative mt-1">
              <Input
                value={data.config?.[field.name] || ''}
                onChange={(e) => onUpdate({ ...data, config: { ...data.config, [field.name]: e.target.value } })}
                placeholder={field.placeholder}
              />
              {field.templateVar && (
                <TemplateVarButton
                  fields={fields}
                  onInsert={(path) => {
                    const current = data.config?.[field.name] || '';
                    onUpdate({ ...data, config: { ...data.config, [field.name]: `${current}{{${path}}}` } });
                  }}
                />
              )}
            </div>
          )}
          {field.description && <p className="text-xs text-gray-400 mt-1">{field.description}</p>}
        </div>
      ))}

      {!selectedAction?.configSchema && data.actionType && (
        <div>
          <Label>Configuration (JSON)</Label>
          <Textarea
            className="mt-1 font-mono text-xs"
            defaultValue={JSON.stringify(data.config || {}, null, 2)}
            onBlur={(e) => {
              try {
                onUpdate({ ...data, config: JSON.parse(e.target.value) });
              } catch { /* will be caught by save validation */ }
            }}
            rows={4}
            placeholder='{ "key": "value" }'
          />
        </div>
      )}

      <div>
        <Label>On Error</Label>
        <Select
          value={data.onError || ERROR_BEHAVIORS.STOP}
          onValueChange={(val) => onUpdate({ ...data, onError: val })}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ERROR_BEHAVIORS).map(([key, value]) => (
              <SelectItem key={value} value={value}>{ERROR_BEHAVIOR_LABELS[value]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const MIN_WIDTH = 280;
const MAX_WIDTH = 700;

const NodeConfigPanel = ({ node, eventTypes = [], actionTypes = [], fields = [], onUpdate, onDelete, onClose }) => {
  const nodeType = node.type;
  const defaultWidth = nodeType === NODE_TYPES.CONDITION ? 384 : 320;
  const [width, setWidth] = useState(defaultWidth);
  const dragRef = useRef(null);

  const listenersRef = useRef(null);

  useEffect(() => {
    return () => {
      if (listenersRef.current) {
        document.removeEventListener('mousemove', listenersRef.current.move);
        document.removeEventListener('mouseup', listenersRef.current.up);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };

    const handleMouseMove = (e) => {
      const { startX, startWidth } = dragRef.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth - (e.clientX - startX)));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      listenersRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    listenersRef.current = { move: handleMouseMove, up: handleMouseUp };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width]);

  const titleMap = {
    [NODE_TYPES.TRIGGER]: 'Configure Trigger',
    [NODE_TYPES.CONDITION]: 'Configure Condition',
    [NODE_TYPES.ACTION]: 'Configure Action',
  };

  const borderColorMap = {
    [NODE_TYPES.TRIGGER]: 'border-l-blue-500',
    [NODE_TYPES.CONDITION]: 'border-l-amber-500',
    [NODE_TYPES.ACTION]: 'border-l-green-500',
  };

  return (
    <div className={`relative shrink-0 bg-white border-l-4 ${borderColorMap[nodeType]} overflow-y-auto`} style={{ width }}>
      {/* Resize handle */}
      <div
        className="absolute top-0 left-0 w-1 h-full cursor-col-resize hover:bg-blue-400 active:bg-blue-500 z-10 transition-colors"
        onMouseDown={handleMouseDown}
      />
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-medium text-sm">{titleMap[nodeType]}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X size={14} />
        </Button>
      </div>

      <div className="p-4">
        {nodeType === NODE_TYPES.TRIGGER && (
          <TriggerConfigFields data={node.data} eventTypes={eventTypes} onUpdate={onUpdate} />
        )}
        {nodeType === NODE_TYPES.CONDITION && (
          <ConditionConfigFields data={node.data} onUpdate={onUpdate} fields={fields} />
        )}
        {nodeType === NODE_TYPES.ACTION && (
          <ActionConfigFields data={node.data} actionTypes={actionTypes} onUpdate={onUpdate} fields={fields} />
        )}

        <div className="mt-6 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={onDelete}
          >
            <Trash2 size={14} className="mr-1" />
            Delete Node
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigPanel;
